// src/pages/CreateAppointment.js
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiCheck, FiArrowLeft, FiCalendar } from 'react-icons/fi';
import { useApi } from '../hooks/useApi';
import { useModal } from '../context/ModalContext'; // <-- IMPORTED

const CreateAppointment = ({ tickets, communities, technicians, onAddAppointment }) => {
    const { ticketId } = useParams();
    const navigate = useNavigate();
    const callApi = useApi();
    const { showAlert } = useModal(); // <-- HOOK

    // Find the ticket and community based on the URL parameter
    const ticket = useMemo(() => tickets.find(t => t._id === ticketId), [tickets, ticketId]);
    const community = useMemo(() => {
        if (!ticket) return null;
        return communities.find(c => c.name === ticket.client);
    }, [ticket, communities]);

    const today = new Date().toISOString().split('T')[0];
    const initialState = {
        date: today,
        startTime: '09:00',
        endTime: '11:00',
        technicianIds: ticket?.assignedTechnicianIds || [],
        notes: ''
    };
    
    const [formData, setFormData] = useState(initialState);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [busyTechnicianIds, setBusyTechnicianIds] = useState([]);

    useEffect(() => {
        setFormData({
            date: today,
            startTime: '09:00',
            endTime: '11:00',
            technicianIds: ticket?.assignedTechnicianIds || [],
            notes: ''
        });
    }, [ticketId, ticket]);

    useEffect(() => {
        const fetchAvailability = async () => {
            if (!formData.date) return;
            const result = await callApi(`appointments/availability?date=${formData.date}`);
            if (result.success) {
                setBusyTechnicianIds(result.data.busyTechnicianIds || []);
            } else {
                console.error("Failed to fetch technician availability:", result.error);
                setBusyTechnicianIds([]);
            }
        };
        fetchAvailability();
    }, [formData.date, callApi]);

    if (!ticket || !community) {
        return (
            <div className="page-container">
                <div className="page-header">
                    <h2 className="page-title">Loading...</h2>
                </div>
                <p>Loading ticket information or ticket not found.</p>
            </div>
        );
    }
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleTechnicianSelect = (techId) => {
        setFormData(prev => ({
            ...prev,
            technicianIds: prev.technicianIds.includes(techId)
                ? prev.technicianIds.filter(id => id !== techId)
                : [...prev.technicianIds, techId]
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        if (formData.technicianIds.length === 0) {
            showAlert('Please select at least one technician.', 'Validation Error'); // <-- REPLACED ALERT
            setIsSubmitting(false);
            return;
        }

        const startDateTime = new Date(`${formData.date}T${formData.startTime}`);
        const endDateTime = new Date(`${formData.date}T${formData.endTime}`);
        
        if (endDateTime <= startDateTime) {
            showAlert('End time must be after the start time.', 'Validation Error'); // <-- REPLACED ALERT
            setIsSubmitting(false);
            return;
        }

        const appointmentData = {
            ticketId: ticket._id,
            communityId: community._id,
            title: ticket.subject,
            startTime: startDateTime.toISOString(),
            endTime: endDateTime.toISOString(),
            technicianIds: formData.technicianIds,
            notes: formData.notes
        };

        const result = await onAddAppointment(appointmentData);
        if (result.success) {
            navigate(`/tickets/${ticketId}`);
        } else {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    return (
        <div className="page-container">
            <div className="page-header">
                <div className="header-left-details">
                    <button onClick={handleCancel} className="back-btn" title="Go Back">
                        <FiArrowLeft />
                    </button>
                    <div>
                        <h2 className="page-title">Schedule Service</h2>
                        <span className="page-subtitle">For Ticket #{ticket._id.substring(0, 7)}: {ticket.subject}</span>
                    </div>
                </div>
            </div>

            <div className="card">
                <form onSubmit={handleSubmit}>
                    <div className="card-body" style={{ padding: '2rem' }}>
                        <div className="form-row">
                             <div className="form-group">
                                <label htmlFor="date">Date</label>
                                <input type="date" id="date" name="date" value={formData.date} onChange={handleChange} required className="form-input"/>
                            </div>
                            <div className="form-group">
                                <label htmlFor="startTime">Start Time</label>
                                <input type="time" id="startTime" name="startTime" value={formData.startTime} onChange={handleChange} required className="form-input"/>
                            </div>
                            <div className="form-group">
                                <label htmlFor="endTime">End Time</label>
                                <input type="time" id="endTime" name="endTime" value={formData.endTime} onChange={handleChange} required className="form-input"/>
                            </div>
                        </div>

                         <div className="form-group">
                             <label className="form-label">Assign Technicians</label>
                             <div className="selection-list" style={{ maxHeight: '250px', border: '1px solid var(--gray-300)', borderRadius: '6px', overflowY: 'auto' }}>
                                {technicians.map(tech => {
                                    const isSelected = formData.technicianIds.includes(tech._id);
                                    const isBusy = busyTechnicianIds.includes(tech._id);
                                    return (
                                        <div 
                                            key={tech._id} 
                                            className={`selection-list-item ${isSelected ? 'selected' : ''} ${isBusy ? 'unavailable' : ''}`} 
                                            onClick={() => handleTechnicianSelect(tech._id)}
                                        >
                                            <div className="item-main-info">
                                                <p className="item-title">{tech.name}</p>
                                                <p className="item-subtitle">{tech.specializations.join(', ')}</p>
                                            </div>
                                            <div className="item-meta-info">
                                                {isBusy && <span className="status-badge status-on-leave">Busy</span>}
                                                {isSelected && <FiCheck className="selection-checkmark" />}
                                            </div>
                                        </div>
                                    )
                                })}
                             </div>
                        </div>

                         <div className="form-group">
                            <label htmlFor="notes">Scheduling Notes</label>
                            <textarea id="notes" name="notes" rows="4" value={formData.notes} onChange={handleChange} className="form-textarea" placeholder="e.g., Customer will meet tech on-site. Gate code is #1234."></textarea>
                        </div>
                    </div>
                    <div className="card-footer" style={{ justifyContent: 'flex-end', display: 'flex', gap: '0.75rem' }}>
                        <button type="button" className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
                        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                            <FiCalendar className="btn-icon" />
                            {isSubmitting ? 'Scheduling...' : 'Create Appointment'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateAppointment;